import React from 'react';
import Plot from 'react-plotly.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, Users, BarChart3, Activity } from 'lucide-react';
import ChartExportButton from '@/components/shared/ChartExportButton';
import { MetricTile } from '@/components/shared/MetricTile';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  plotlyChartChrome,
  recessionAnnotations,
  usePlotlyTheme,
} from '@/lib/plotlyTheme';

interface WageHistory {
  year: number;
  occ_code: string;
  occ_title: string;
  employment: number;
  a_mean: number;
  a_median?: number;
}

interface EconomicContext {
  year: number;
  area_code: string;
  unemployment_rate?: number;
  recession_flag: boolean;
}

interface EmploymentTrendsProps {
  wageHistory: WageHistory[];
  economicContext?: EconomicContext[];
  occupationTitle?: string;
  areaName?: string;
}

export const EmploymentTrends: React.FC<EmploymentTrendsProps> = ({
  wageHistory,
  economicContext = [],
  occupationTitle = "Unknown Occupation",
  areaName = "Unknown Area"
}) => {
  const isMobile = useIsMobile();
  const { colors, themeKey } = usePlotlyTheme();

  if (!wageHistory || wageHistory.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Employment Trends Analysis
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center text-muted-foreground py-8">
            No employment data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const years = wageHistory.map(d => d.year);
  const employment = wageHistory.map(d => d.employment);
  
  // Calculate employment growth metrics
  const latestYear = Math.max(...years);
  const earliestYear = Math.min(...years);
  const latestEmployment = wageHistory.find(d => d.year === latestYear)?.employment || 0;
  const earliestEmployment = wageHistory.find(d => d.year === earliestYear)?.employment || 0;
  const totalGrowth = earliestEmployment > 0 ? ((latestEmployment - earliestEmployment) / earliestEmployment) * 100 : 0;
  const annualGrowth = Math.pow(latestEmployment / earliestEmployment, 1 / (latestYear - earliestYear)) - 1;

  // Calculate volatility (standard deviation of year-over-year changes)
  const yoyChanges = [];
  for (let i = 1; i < wageHistory.length; i++) {
    const current = wageHistory[i].employment;
    const previous = wageHistory[i - 1].employment;
    const change = ((current - previous) / previous) * 100;
    yoyChanges.push(change);
  }
  const avgChange = yoyChanges.reduce((sum, change) => sum + change, 0) / yoyChanges.length;
  const volatility = Math.sqrt(yoyChanges.reduce((sum, change) => sum + Math.pow(change - avgChange, 2), 0) / yoyChanges.length);

  // Recession periods for shading
  const recessionYears = economicContext
    .filter(ec => ec.recession_flag)
    .map(ec => ec.year);

  const traces = [];

  // Main employment line
  traces.push({
    x: years,
    y: employment,
    type: 'scatter',
    mode: 'lines+markers',
    name: 'Total Employment',
    line: { color: '#22c55e', width: 3 },
    marker: { color: '#22c55e', size: 6 },
    hovertemplate: 
      '<b>%{x}</b><br>' +
      'Employment: %{y:,.0f}<br>' +
      '<extra></extra>'
  });

  // Add employment trend line
  {
    const n = years.length;
    const sumX = years.reduce((a, b) => a + b, 0);
    const sumY = employment.reduce((a, b) => a + b, 0);
    const sumXY = years.reduce((sum, x, i) => sum + x * employment[i], 0);
    const sumXX = years.reduce((sum, x) => sum + x * x, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    
    const trendLine = years.map(x => slope * x + intercept);
    
    traces.push({
      x: years,
      y: trendLine,
      type: 'scatter',
      mode: 'lines',
      name: 'Trend Line',
      line: { color: '#6366f1', width: 2, dash: 'dash' },
      hovertemplate: 
        '<b>%{x}</b><br>' +
        'Trend: %{y:,.0f}<br>' +
        '<extra></extra>'
    });
  }

  // Employment growth rate (secondary axis)
  if (yoyChanges.length > 0) {
    traces.push({
      x: years.slice(1),
      y: yoyChanges,
      type: 'scatter',
      mode: 'lines',
      name: 'Growth Rate (%)',
      line: { color: '#f59e0b', width: 2 },
      yaxis: 'y2',
      hovertemplate: 
        '<b>%{x}</b><br>' +
        'Growth: %{y:+.1f}%<br>' +
        '<extra></extra>'
    });
  }

  // Recession shading
  recessionYears.forEach(year => {
    if (years.includes(year)) {
      traces.push({
        x: [year - 0.5, year + 0.5, year + 0.5, year - 0.5],
        y: [0, 0, 1, 1],
        fill: 'toself',
        fillcolor: colors.recessionFill,
        line: { color: 'transparent' },
        showlegend: false,
        hoverinfo: 'skip',
        yaxis: 'y3',
        name: 'Recession'
      });
    }
  });

  const chrome = plotlyChartChrome(colors, { isMobile });

  const layout = {
    title: {
      text: `${occupationTitle} - Employment Trends in ${areaName}`,
      font: chrome.titleFont
    },
    xaxis: {
      title: chrome.axisTitle(isMobile ? undefined : 'Year'),
      type: 'linear',
      dtick: isMobile ? 5 : 2,
      nticks: isMobile ? 7 : undefined,
      showgrid: true,
      tickangle: isMobile ? -45 : 0,
      ...chrome.axisDefaults
    },
    yaxis: {
      title: chrome.axisTitle(isMobile ? undefined : 'Total Employment'),
      tickformat: ',.0f',
      showgrid: true,
      ...chrome.axisDefaults
    },
    yaxis2: {
      title: chrome.axisTitle(isMobile ? undefined : 'Employment Growth (%)'),
      tickformat: '+.1f',
      side: 'right',
      overlaying: 'y',
      showgrid: false,
      ...chrome.axisDefaults
    },
    yaxis3: {
      overlaying: 'y',
      range: [0, 1],
      showgrid: false,
      showticklabels: false,
      zeroline: false
    },
    hovermode: 'x unified',
    hoverlabel: chrome.hoverlabel,
    showlegend: true,
    legend: chrome.legend(),
    margin: isMobile
      ? { l: 48, r: 48, t: 48, b: 88 }
      : { l: 80, r: 88, t: 60, b: 60 },
    paper_bgcolor: chrome.paper_bgcolor,
    plot_bgcolor: chrome.plot_bgcolor,
    font: chrome.font,
    annotations: recessionAnnotations(recessionYears, colors, { enabled: !isMobile })
  };

  const config = {
    displayModeBar: !isMobile,
    modeBarButtonsToAdd: [
      'pan2d',
      'autoScale2d',
      'resetScale2d',
      'zoomIn2d',
      'zoomOut2d'
    ],
    modeBarButtonsToRemove: ['lasso2d', 'select2d'],
    displaylogo: false,
    responsive: true,
    toImageButtonOptions: {
      format: 'png',
      filename: `${occupationTitle.replace(/[^a-z0-9]/gi, '_')}_employment_trends`,
      height: 500,
      width: 800,
      scale: 2
    }
  };

  const growthTone = totalGrowth >= 0 ? 'green' : 'red';
  const annualTone = annualGrowth >= 0 ? 'green' : 'red';
  const volatilityTone = volatility > 10 ? 'red' : volatility > 5 ? 'yellow' : 'green';
  const volatilityLabel = volatility > 10 ? 'High' : volatility > 5 ? 'Medium' : 'Low';
  const GrowthIcon =
    totalGrowth > 2 ? TrendingUp : totalGrowth < -2 ? TrendingDown : Minus;

  return (
    <Card className="relative">
      <CardHeader>
        <div className="flex w-full min-w-0 items-start justify-between gap-3">
          <CardTitle className="flex min-w-0 flex-1 items-start gap-2 text-lg leading-snug">
            <Users className="mt-0.5 h-5 w-5 shrink-0" />
            <span className="min-w-0 break-words">Employment Trends Analysis</span>
          </CardTitle>
          <ChartExportButton
            chartId="employment-trends-chart"
            filename={`employment-trends-${occupationTitle?.replace(/[^a-z0-9]/gi, '_') || 'chart'}`}
          />
        </div>
        <CardDescription>
          Employment analysis with {wageHistory.length} years of data
          {recessionYears.length > 0 && ` • ${recessionYears.length} recession periods highlighted`}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 relative">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MetricTile
            tone="blue"
            icon={Users}
            badge="Latest"
            value={latestEmployment.toLocaleString()}
            label="Current Employment"
          />
          <MetricTile
            tone={growthTone}
            icon={GrowthIcon}
            badge="Total"
            value={`${totalGrowth >= 0 ? '+' : ''}${totalGrowth.toFixed(1)}%`}
            label="Total Growth"
          />
          <MetricTile
            tone={annualTone}
            icon={BarChart3}
            badge="Annual"
            value={`${annualGrowth >= 0 ? '+' : ''}${(annualGrowth * 100).toFixed(1)}%`}
            label="Annual Growth"
          />
          <MetricTile
            tone={volatilityTone}
            icon={Activity}
            badge={volatilityLabel}
            value={`${volatility.toFixed(1)}%`}
            label="Volatility"
          />
        </div>

        {/* Interactive Chart */}
        <div id="employment-trends-chart" className="h-96 min-w-0 w-full overflow-x-auto">
          <Plot
            data={traces}
            layout={layout}
            config={config}
            style={{ width: '100%', height: '100%' }}
            useResizeHandler={true}
            key={`${themeKey}-${isMobile ? 'mobile' : 'desktop'}`}
          />
        </div>

      </CardContent>
    </Card>
  );
};